<script setup>
import { ref, computed, watch } from 'vue';
import TreeTable from 'primevue/treetable';
import Column from 'primevue/column';
import Checkbox from 'primevue/checkbox';
import { useRbacStore } from '@/stores/rbac.js';

const props = defineProps({
  itemType: {
    type: Object,
    required: true
  },
  entityType: {
    type: String,
    required: true,
    validator: (value) => ['document', 'file'].includes(value)
  }
});

const emit = defineEmits(['update:permissions']);

const rbacStore = useRbacStore();

// Простой массив ID позиций с доступом
const selectedRoleIds = ref([]);

// Инициализируем права из role_ids
watch(() => props.itemType?.role_ids, (roleIds) => {
  selectedRoleIds.value = roleIds ? [...roleIds] : [];
}, { immediate: true });

// Преобразуем дерево отделов в формат TreeTable с должностями
const treeNodes = computed(() => {
  return convertDepartmentsToTreeNodes(rbacStore.departmentsTree);
});

function convertDepartmentsToTreeNodes(departments) {
  return departments.map(dept => {
    const positions = rbacStore.positions.filter(pos => pos.department_id === dept.id);

    // Создаём узел отдела
    const deptNode = {
      key: `dept-${dept.id}`,
      data: {
        id: dept.id,
        name: dept.name,
        type: 'department',
        isDepartment: true
      },
      children: []
    };

    // Добавляем позиции как дочерние элементы ПЕРЕД дочерними отделами
    positions.forEach(pos => {
      deptNode.children.push({
        key: `pos-${pos.id}`,
        data: {
          id: pos.id,
          name: pos.name,
          type: 'position',
          isDepartment: false,
          departmentId: dept.id
        },
        children: []
      });
    });

    // Рекурсивно обрабатываем дочерние отделы
    if (dept.children && dept.children.length > 0) {
      const childDepts = convertDepartmentsToTreeNodes(dept.children);
      deptNode.children.push(...childDepts);
    }

    return deptNode;
  });
}

// Проверка доступа для позиции
function hasAccess(positionId) {
  return selectedRoleIds.value.includes(positionId);
}

// Проверка состояния отдела (все/частично/нет)
function getDepartmentCheckState(deptNode) {
  const positionIds = collectPositionIds(deptNode);
  if (positionIds.length === 0) return 'none';

  const checkedCount = positionIds.filter(id => selectedRoleIds.value.includes(id)).length;

  if (checkedCount === 0) return 'none';
  if (checkedCount === positionIds.length) return 'all';
  return 'partial';
}

// Собрать все ID позиций в отделе (включая дочерние отделы)
function collectPositionIds(deptNode) {
  const ids = [];

  function traverse(node) {
    if (node.data.type === 'position') {
      ids.push(node.data.id);
    }
    if (node.children && node.children.length > 0) {
      node.children.forEach(child => traverse(child));
    }
  }

  traverse(deptNode);
  return ids;
}

// Переключить доступ для позиции
function togglePosition(positionId) {
  const index = selectedRoleIds.value.indexOf(positionId);
  if (index > -1) {
    selectedRoleIds.value.splice(index, 1);
  } else {
    selectedRoleIds.value.push(positionId);
  }
  emitPermissions();
}

// Переключить доступ для отдела (каскадно)
function toggleDepartment(deptNode) {
  const positionIds = collectPositionIds(deptNode);
  const state = getDepartmentCheckState(deptNode);

  if (state === 'all') {
    selectedRoleIds.value = selectedRoleIds.value.filter(id => !positionIds.includes(id));
  } else {
    positionIds.forEach(id => {
      if (!selectedRoleIds.value.includes(id)) {
        selectedRoleIds.value.push(id);
      }
    });
  }
  emitPermissions();
}

// Отправить обновленные права родителю
function emitPermissions() {
  emit('update:permissions', selectedRoleIds.value);
}

defineExpose({
  selectedRoleIds
});
</script>

<template>
  <div class="h-full flex flex-col">
    <TreeTable
      :value="treeNodes"
      scrollable
      scroll-height="400px"
      class="text-sm"
    >
      <Column field="name" header="Роль" expander style="min-width: 300px">
        <template #body="{ node }">
          {{ node.data.name }}
        </template>
      </Column>

      <Column header="Доступно" style="width: 120px; text-align: center;">
        <template #body="{ node }">
          <Checkbox
            v-if="!node.data.isDepartment"
            :model-value="hasAccess(node.data.id)"
            :binary="true"
            @change="togglePosition(node.data.id)"
          />
          <Checkbox
            v-else
            :model-value="getDepartmentCheckState(node) === 'all'"
            :indeterminate="getDepartmentCheckState(node) === 'partial'"
            :binary="true"
            @change="toggleDepartment(node)"
          />
        </template>
      </Column>
    </TreeTable>
  </div>
</template>

<style scoped>
</style>
